import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { MODO_CREDITO_UNIFICADO, MODO_SEM_FASE_ANALISE } from '../config';
import ModalDetalhePedido from '../components/ModalDetalhePedido';

const brl = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const normalizarBusca = v => String(v ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim();

const contemBusca = (campos, busca) => {
  const termo = normalizarBusca(busca);
  if (!termo) return true;
  return campos.some(c => normalizarBusca(c).includes(termo));
};

const dataFmt = d => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const [y, m, day] = s.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
};

const mesFmt = m => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${nomes[parseInt(mo) - 1]}/${y}`;
};

const FASES = {
  DEVOLUCAO:           { cls: 'badge-negativo',  label: 'Devolução' },
  BAIXADO:             { cls: 'badge-positivo',  label: 'Baixado' },
  PAGO:                { cls: 'badge-positivo',  label: 'Pago' },
  EM_ANALISE:          { cls: 'badge-neutro',    label: 'Em Análise' },
  CREDITO_INTERNO:     { cls: 'badge-pendente',  label: 'Crédito Interno' },
  DISPONIVEL_SAQUE:    { cls: 'badge-filial',    label: 'Disponível Saque' },
  INDISPONIVEL:        { cls: 'badge-neutro',    label: 'Indisponível' },
  CREDITO_ABATIMENTO:  { cls: 'badge-neutro',    label: 'Crédito de Abatimento' },
};

const CODIGO_TIPO = codigo => {
  if (/creind-dev/i.test(codigo || '')) return { cls: 'badge-negativo', label: 'Devolução' };
  if (/creind-can/i.test(codigo || '')) return { cls: 'badge-pendente', label: 'Cancelamento' };
  return null;
};

const faseEfetiva = r => {
  if (CODIGO_TIPO(r.codigo)) return 'CREDITO_ABATIMENTO';
  if (r.info === 'Baixa' && r.pagamento != null) return 'BAIXADO';
  // Fases arquivadas (UI agrega tudo em DISPONIVEL_SAQUE):
  if (MODO_CREDITO_UNIFICADO && r.fase === 'CREDITO_INTERNO') return 'DISPONIVEL_SAQUE';
  if (MODO_SEM_FASE_ANALISE  && r.fase === 'EM_ANALISE')      return 'DISPONIVEL_SAQUE';
  return r.fase;
};

function CodigoBadge({ codigo }) {
  const bt = CODIGO_TIPO(codigo);
  if (!bt) return null;
  return <span className={bt.cls} style={{ marginLeft: 4 }}>{bt.label}</span>;
}

function FaseBadge({ fase, diasEmissao, pagamento, info, codigo }) {
  let fase_;
  if (CODIGO_TIPO(codigo)) fase_ = 'CREDITO_ABATIMENTO';
  else if (info === 'Baixa' && pagamento != null) fase_ = 'BAIXADO';
  else if (MODO_CREDITO_UNIFICADO && fase === 'CREDITO_INTERNO') fase_ = 'DISPONIVEL_SAQUE';
  else if (MODO_SEM_FASE_ANALISE  && fase === 'EM_ANALISE')      fase_ = 'DISPONIVEL_SAQUE';
  else fase_ = fase;

  const { cls, label } = FASES[fase_] || { cls: 'badge-neutro', label: fase_ };
  const tooltip = {
    EM_ANALISE:      () => `Libera em ${30 - diasEmissao} dias`,
    CREDITO_INTERNO: () => `Saque em ${60 - diasEmissao} dias`,
    PAGO:            () => pagamento ? `Recebido em ${dataFmt(pagamento)}` : null,
  }[fase_]?.();
  return (
    <span className={cls} title={tooltip || ''}>
      {label}{tooltip ? <span style={{ opacity: 0.7, fontSize: '0.78rem' }}> · {tooltip}</span> : null}
    </span>
  );
}

function FiltroTabela({ valor, onChange, placeholder }) {
  return (
    <div className="p-3 border-bottom bg-white">
      <div className="input-group" style={{ maxWidth: 420 }}>
        <span className="input-group-text bg-white">
          <i className="bi bi-search"></i>
        </span>
        <input
          type="search"
          className="form-control"
          value={valor}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {valor && (
          <button type="button" className="btn btn-outline-secondary" onClick={() => onChange('')} title="Limpar filtro">
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>
    </div>
  );
}

function SortTh({ col, sort, onSort, children, className, style }) {
  const ativo = sort.col === col;
  const icone = ativo
    ? (sort.dir === 'asc' ? 'bi-sort-up' : 'bi-sort-down')
    : 'bi-arrow-down-up';
  return (
    <th
      className={className}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={() => onSort(col)}
    >
      {children}{' '}
      <i className={`bi ${icone}`} style={{ opacity: ativo ? 1 : 0.3, fontSize: '0.72rem' }} />
    </th>
  );
}

const toggleSort = (setter, col) =>
  setter(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));

const dateToNum = v => {
  if (v == null) return null;
  const t = new Date(v).getTime();
  return isNaN(t) ? null : t;
};

const aplicarSort = (lista, sort, getVal) => {
  if (!sort.col) return lista;
  return [...lista].sort((a, b) => {
    const va = getVal(a, sort.col);
    const vb = getVal(b, sort.col);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const r = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
    return sort.dir === 'asc' ? r : -r;
  });
};

// Cards de fase. Quando crédito unificado, "Crédito Interno" some. Quando
// sem fase de análise, "Em Análise" também some — tudo em "Disponível para Saque".
function montarCards() {
  const cards = [
    { key: 'disponivel_saque', label: 'Disponível para Saque', cor: 'var(--primary)' },
  ];
  if (!MODO_CREDITO_UNIFICADO) cards.push({ key: 'credito_interno', label: 'Crédito Interno', cor: 'var(--accent)' });
  if (!MODO_SEM_FASE_ANALISE)  cards.push({ key: 'em_analise',      label: 'Em Análise',       cor: '#888' });
  cards.push({ key: 'pago', label: 'Já Recebido', cor: 'var(--success)' });
  return cards;
}
const CARDS = montarCards();

const ZERO_TOTAIS = { disponivel_saque: 0, credito_interno: 0, em_analise: 0, pago: 0, total: 0, quantidade: 0 };

// Agrega fases arquivadas em disponivel_saque pra exibição (SQL continua segregando).
function unificarSeNecessario(linha) {
  if (!MODO_CREDITO_UNIFICADO && !MODO_SEM_FASE_ANALISE) return linha;
  const credInt = MODO_CREDITO_UNIFICADO ? (linha.credito_interno || 0) : 0;
  const emAnal  = MODO_SEM_FASE_ANALISE  ? (linha.em_analise || 0)      : 0;
  return {
    ...linha,
    disponivel_saque: (linha.disponivel_saque || 0) + credInt + emAnal,
    credito_interno: MODO_CREDITO_UNIFICADO ? 0 : (linha.credito_interno || 0),
    em_analise:      MODO_SEM_FASE_ANALISE  ? 0 : (linha.em_analise || 0),
  };
}

export default function Comissoes() {
  const { user } = useAuth();
  const isArquiteto = user?.tipo === 'arquiteto';

  const hoje = new Date().toISOString().split('T')[0];
  const trintaDiasAtras = (() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0];
  })();

  const [aba, setAba] = useState('sintetica');
  const [oidFiltro, setOidFiltro] = useState('');
  const [dataInicio, setDataInicio] = useState(trintaDiasAtras);
  const [dataFim, setDataFim] = useState(hoje);
  const [pagamentoInicio, setPagamentoInicio] = useState('');
  const [pagamentoFim, setPagamentoFim] = useState('');
  const [filtroSintetica, setFiltroSintetica] = useState('');
  const [filtroAnalitica, setFiltroAnalitica] = useState('');
  const [filtroDetalhe, setFiltroDetalhe] = useState('');
  const [arquitetos, setArquitetos] = useState([]);

  const [sintetica, setSintetica] = useState(null);
  const [analitica, setAnalitica] = useState([]);
  const [detalhe, setDetalhe] = useState(null);
  const [numpedDetalhe, setNumpedDetalhe] = useState(null); // dispara ModalDetalhePedido
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Extrai numped do código CREIND (apenas vendas normais — não DEV/CAN)
  const extrairNumped = (codigo) => {
    if (!codigo || /CREIND-(dev|can)/i.test(codigo)) return null;
    const m = String(codigo).match(/^CREIND-(\d+)/);
    return m ? parseInt(m[1]) : null;
  };

  const [sortSin, setSortSin] = useState({ col: null, dir: 'asc' });
  const [sortAna, setSortAna] = useState({ col: null, dir: 'asc' });
  const [sortDet, setSortDet] = useState({ col: null, dir: 'asc' });

  useEffect(() => {
    if (isArquiteto) return;
    api.get('/arquitetos').then(r => setArquitetos(r.data)).catch(() => {});
  }, [isArquiteto]);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro('');
    const qs = new URLSearchParams();
    if (oidFiltro)   qs.set('oid', oidFiltro);
    if (dataInicio)  qs.set('dataInicio', dataInicio);
    if (dataFim)     qs.set('dataFim', dataFim);
    const qsAna = new URLSearchParams(qs);
    if (pagamentoInicio) qsAna.set('pagamentoInicio', pagamentoInicio);
    if (pagamentoFim)    qsAna.set('pagamentoFim', pagamentoFim);
    const qstrSin = qs.toString()    ? `?${qs.toString()}`    : '';
    const qstrAna = qsAna.toString() ? `?${qsAna.toString()}` : '';
    Promise.all([
      api.get(`/comissoes/sintetica${qstrSin}`),
      api.get(`/comissoes/analitica${qstrAna}`),
    ])
      .then(([s, a]) => { setSintetica(s.data); setAnalitica(a.data); })
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar comissões'))
      .finally(() => setCarregando(false));
  }, [oidFiltro, dataInicio, dataFim, pagamentoInicio, pagamentoFim]);

  useEffect(() => { carregar(); }, [carregar]);

  const totais = useMemo(() => {
    if (!sintetica?.dados?.length) return ZERO_TOTAIS;
    return sintetica.dados.reduce((acc, raw) => {
      const r = unificarSeNecessario(raw);
      return {
        disponivel_saque: acc.disponivel_saque + (r.disponivel_saque || 0),
        credito_interno:  acc.credito_interno  + (r.credito_interno  || 0),
        em_analise:       acc.em_analise       + (r.em_analise       || 0),
        pago:             acc.pago             + (r.pago             || 0),
        total:            acc.total            + (r.total            || 0),
        quantidade:       acc.quantidade       + (r.quantidade       || 0),
      };
    }, { ...ZERO_TOTAIS });
  }, [sintetica]);

  const mostrarArquiteto = !isArquiteto && !oidFiltro;

  const sinteticaFiltrada = useMemo(() => {
    if (!sintetica?.dados?.length) return [];
    const filtrada = sintetica.dados.map(unificarSeNecessario).filter(r => contemBusca([
      sintetica.tipo === 'mensal' ? mesFmt(r.mes) : r.nome,
      r.disponivel_saque,
      brl(r.disponivel_saque),
      r.credito_interno,
      brl(r.credito_interno),
      r.em_analise,
      brl(r.em_analise),
      r.pago,
      brl(r.pago),
      r.total,
      brl(r.total),
      r.quantidade,
    ], filtroSintetica));
    return aplicarSort(filtrada, sortSin, (r, col) =>
      col === 'label' ? (sintetica.tipo === 'mensal' ? r.mes : r.nome) : r[col]
    );
  }, [filtroSintetica, sintetica, sortSin]);

  const totaisSinteticaFiltrada = useMemo(() => {
    if (!sinteticaFiltrada.length) return ZERO_TOTAIS;
    return sinteticaFiltrada.reduce((acc, r) => ({
      disponivel_saque: acc.disponivel_saque + (r.disponivel_saque || 0),
      credito_interno:  acc.credito_interno  + (r.credito_interno  || 0),
      em_analise:       acc.em_analise       + (r.em_analise       || 0),
      pago:             acc.pago             + (r.pago             || 0),
      total:            acc.total            + (r.total            || 0),
      quantidade:       acc.quantidade       + (r.quantidade       || 0),
    }), { ...ZERO_TOTAIS });
  }, [sinteticaFiltrada]);

  const analiticaFiltrada = useMemo(() => {
    const filtrada = analitica.filter(r => contemBusca([
      r.nome,
      dataFmt(r.emissao),
      r.codigo,
      r.credito,
      brl(r.credito),
      r.info,
      r.fpagamento,
      r.caixa,
      FASES[faseEfetiva(r)]?.label || r.fase,
      dataFmt(r.pagamento),
    ], filtroAnalitica));
    return aplicarSort(filtrada, sortAna, (r, col) => {
      if (col === 'fase') return FASES[faseEfetiva(r)]?.label || r.fase;
      if (col === 'emissao' || col === 'pagamento') return dateToNum(r[col]);
      return r[col];
    });
  }, [analitica, filtroAnalitica, sortAna]);

  const registrosDetalheBase = useMemo(() => {
    if (!detalhe) return [];

    if (detalhe.tipo === 'mensal') {
      return analitica.filter(r => {
        if (!r.emissao) return false;
        const emissao = typeof r.emissao === 'string' ? r.emissao : new Date(r.emissao).toISOString();
        return emissao.startsWith(detalhe.mes);
      });
    }

    return analitica.filter(r => String(r.oid) === String(detalhe.oid));
  }, [analitica, detalhe]);

  const registrosDetalhe = useMemo(() => {
    const filtrada = registrosDetalheBase.filter(r => contemBusca([
      r.nome,
      dataFmt(r.emissao),
      r.codigo,
      r.credito,
      brl(r.credito),
      r.info,
      r.fpagamento,
      r.caixa,
      FASES[faseEfetiva(r)]?.label || r.fase,
      dataFmt(r.pagamento),
    ], filtroDetalhe));
    return aplicarSort(filtrada, sortDet, (r, col) => {
      if (col === 'fase') return FASES[faseEfetiva(r)]?.label || r.fase;
      if (col === 'emissao' || col === 'pagamento') return dateToNum(r[col]);
      return r[col];
    });
  }, [filtroDetalhe, registrosDetalheBase, sortDet]);

  const totalDetalhe = useMemo(
    () => registrosDetalhe.reduce((acc, r) => acc + (r.credito || 0), 0),
    [registrosDetalhe]
  );

  const abrirDetalheSintetica = useCallback((registro) => {
    if (!sintetica) return;

    if (sintetica.tipo === 'mensal') {
      setDetalhe({
        tipo: 'mensal',
        titulo: mesFmt(registro.mes),
        mes: registro.mes,
        mostrarArquiteto,
      });
      return;
    }

    setDetalhe({
      tipo: 'arquiteto',
      titulo: registro.nome,
      oid: registro.oid,
      mostrarArquiteto: false,
    });
  }, [mostrarArquiteto, sintetica]);

  const fecharDetalhe = useCallback(() => {
    setDetalhe(null);
    setFiltroDetalhe('');
    setSortDet({ col: null, dir: 'asc' });
  }, []);

  useEffect(() => {
    if (!detalhe) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') fecharDetalhe();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [detalhe, fecharDetalhe]);

  // Base: label + disp_saque + pago + total + qtd = 5. + crédito interno (se exibido) + em análise (se exibido)
  const colSpanSin = 5 + (MODO_CREDITO_UNIFICADO ? 0 : 1) + (MODO_SEM_FASE_ANALISE ? 0 : 1);
  const colSpanAna = mostrarArquiteto ? 8 : 7;

  return (
    <div>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-cash-coin me-2" style={{ color: 'var(--primary)' }}></i>
        {isArquiteto ? 'Minhas Comissões' : 'Comissões de Arquitetos'}
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
        {isArquiteto
          ? `Comissões por indicação — ${user?.nome}`
          : 'Visão consolidada das comissões por indicação'}
      </p>

      {/* Aviso de corte do histórico — só pro arquiteto. As comissões passaram a
          ser registradas como crédito na conta (que o portal consulta) a partir
          de mar/2026; o histórico anterior fica no sistema da loja. */}
      {isArquiteto && (
        <div
          className="mb-4"
          style={{
            background: '#FFF8E9',
            border: '1px solid #F0D9A8',
            borderLeft: '4px solid var(--accent)',
            borderRadius: 'var(--raio, 12px)',
            padding: '12px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <i className="bi bi-clock-history" style={{ color: 'var(--accent)', fontSize: '1.15rem', marginTop: 1, flexShrink: 0 }} />
          <div style={{ fontSize: '0.85rem', color: '#5c4a1e', lineHeight: 1.5 }}>
            <strong>Sobre o seu histórico:</strong> suas comissões passaram a ser
            registradas aqui no portal a partir de <strong>março de 2026</strong>. Daqui
            pra frente, tudo fica disponível no portal — e também na loja, se quiser
            conferir por lá. Comissões de antes dessa data ficam no histórico da loja.
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="d-flex flex-wrap gap-3 align-items-end mb-4">
        {!isArquiteto && (
          <div>
            <label className="form-label mb-1" style={{ fontSize: '0.8rem', color: '#666' }}>Arquiteto</label>
            <select
              className="form-select"
              style={{ minWidth: 240 }}
              value={oidFiltro}
              onChange={e => setOidFiltro(e.target.value)}
            >
              <option value="">Todos os arquitetos</option>
              {arquitetos.map(a => (
                <option key={a.oid} value={a.oid}>{a.nome}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.8rem', color: '#666' }}>Data inicial</label>
          <input
            type="date"
            className="form-control"
            style={{ minWidth: 160 }}
            value={dataInicio}
            max={dataFim || undefined}
            onChange={e => setDataInicio(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.8rem', color: '#666' }}>Data final</label>
          <input
            type="date"
            className="form-control"
            style={{ minWidth: 160 }}
            value={dataFim}
            min={dataInicio || undefined}
            onChange={e => setDataFim(e.target.value)}
          />
        </div>
        <div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            style={{ height: 38 }}
            onClick={() => { setDataInicio(trintaDiasAtras); setDataFim(hoje); }}
            title="Redefinir para últimos 30 dias"
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i>Últimos 30 dias
          </button>
        </div>
        {aba === 'analitica' && (
          <>
            <div style={{ borderLeft: '1px solid #dee2e6', height: 44, margin: '0 4px' }} />
            <div>
              <label className="form-label mb-1" style={{ fontSize: '0.8rem', color: '#666' }}>Pgto/Baixa (início)</label>
              <input
                type="date"
                className="form-control"
                style={{ minWidth: 160 }}
                value={pagamentoInicio}
                max={pagamentoFim || undefined}
                onChange={e => setPagamentoInicio(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label mb-1" style={{ fontSize: '0.8rem', color: '#666' }}>Pgto/Baixa (fim)</label>
              <input
                type="date"
                className="form-control"
                style={{ minWidth: 160 }}
                value={pagamentoFim}
                min={pagamentoInicio || undefined}
                onChange={e => setPagamentoFim(e.target.value)}
              />
            </div>
            {(pagamentoInicio || pagamentoFim) && (
              <div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ height: 38 }}
                  onClick={() => { setPagamentoInicio(''); setPagamentoFim(''); }}
                  title="Limpar filtro de pagamento/baixa"
                >
                  <i className="bi bi-x-lg me-1"></i>Limpar pgto
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cards de fase */}
      {!carregando && !erro && (
        <div className="row g-3 mb-4">
          {CARDS.map(c => (
            <div key={c.key} className="col-6 col-sm-3">
              <div className="card card-stat p-3" style={{ borderLeftColor: c.cor }}>
                <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {c.label}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: c.cor, marginTop: 4 }}>
                  {brl(totais[c.key])}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${aba === 'sintetica' ? 'active' : ''}`} onClick={() => setAba('sintetica')}>
            <i className="bi bi-bar-chart me-1"></i>Sintética
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${aba === 'analitica' ? 'active' : ''}`} onClick={() => setAba('analitica')}>
            <i className="bi bi-list-ul me-1"></i>Analítica
          </button>
        </li>
      </ul>

      {carregando ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
        </div>
      ) : erro ? (
        <div className="alert alert-danger">{erro}</div>
      ) : (
        <>
          {/* Sintética */}
          {aba === 'sintetica' && sintetica && (
            <div className="card p-0">
              <FiltroTabela
                valor={filtroSintetica}
                onChange={setFiltroSintetica}
                placeholder={`Filtrar por ${sintetica.tipo === 'mensal' ? 'mês, valor ou quantidade' : 'arquiteto, valor ou quantidade'}`}
              />
              <div className="tabela-container">
                <table className="table table-hover table-arq mb-0">
                  <thead>
                    <tr>
                      <SortTh col="label" sort={sortSin} onSort={col => toggleSort(setSortSin, col)}>
                        {sintetica.tipo === 'mensal' ? 'Mês' : 'Arquiteto'}
                      </SortTh>
                      <SortTh col="disponivel_saque" sort={sortSin} onSort={col => toggleSort(setSortSin, col)} className="text-end">
                        {MODO_CREDITO_UNIFICADO ? 'Disponível para Saque' : 'Disponível Saque'}
                      </SortTh>
                      {!MODO_CREDITO_UNIFICADO && (
                        <SortTh col="credito_interno" sort={sortSin} onSort={col => toggleSort(setSortSin, col)} className="text-end">
                          Crédito Interno
                        </SortTh>
                      )}
                      {!MODO_SEM_FASE_ANALISE && (
                        <SortTh col="em_analise" sort={sortSin} onSort={col => toggleSort(setSortSin, col)} className="text-end">
                          Em Análise
                        </SortTh>
                      )}
                      <SortTh col="pago" sort={sortSin} onSort={col => toggleSort(setSortSin, col)} className="text-end">
                        Já Recebido
                      </SortTh>
                      <SortTh col="total" sort={sortSin} onSort={col => toggleSort(setSortSin, col)} className="text-end">
                        Total
                      </SortTh>
                      <SortTh col="quantidade" sort={sortSin} onSort={col => toggleSort(setSortSin, col)} className="text-center">
                        Qtd
                      </SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sinteticaFiltrada.length === 0 ? (
                      <tr><td colSpan={colSpanSin} className="text-center text-muted py-4">Nenhum registro encontrado</td></tr>
                    ) : sinteticaFiltrada.map((r, i) => (
                      <tr
                        key={sintetica.tipo === 'mensal' ? r.mes : (r.oid ?? i)}
                        className="linha-clicavel"
                        onDoubleClick={() => abrirDetalheSintetica(r)}
                        title="Abrir detalhe"
                      >
                        <td className="fw-semibold">
                          {sintetica.tipo === 'mensal' ? mesFmt(r.mes) : r.nome }
                        </td>
                        <td className="text-end" style={{ color: 'var(--primary)' }}>{brl(r.disponivel_saque)}</td>
                        {!MODO_CREDITO_UNIFICADO && (
                          <td className="text-end" style={{ color: 'var(--accent)' }}>{brl(r.credito_interno)}</td>
                        )}
                        {!MODO_SEM_FASE_ANALISE && (
                          <td className="text-end text-muted">{brl(r.em_analise)}</td>
                        )}
                        <td className="text-end" style={{ color: 'var(--success)' }}>{brl(r.pago)}</td>
                        <td className="text-end fw-semibold">{brl(r.total)}</td>
                        <td className="text-center text-muted">{r.quantidade}</td>
                      </tr>
                    ))}
                  </tbody>
                  {sinteticaFiltrada.length > 1 && (
                    <tfoot>
                      <tr style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
                        <td>Total</td>
                        <td className="text-end" style={{ color: 'var(--primary)' }}>{brl(totaisSinteticaFiltrada.disponivel_saque)}</td>
                        {!MODO_CREDITO_UNIFICADO && (
                          <td className="text-end" style={{ color: 'var(--accent)' }}>{brl(totaisSinteticaFiltrada.credito_interno)}</td>
                        )}
                        {!MODO_SEM_FASE_ANALISE && (
                          <td className="text-end text-muted">{brl(totaisSinteticaFiltrada.em_analise)}</td>
                        )}
                        <td className="text-end" style={{ color: 'var(--success)' }}>{brl(totaisSinteticaFiltrada.pago)}</td>
                        <td className="text-end">{brl(totaisSinteticaFiltrada.total)}</td>
                        <td className="text-center text-muted">{totaisSinteticaFiltrada.quantidade}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* Analítica */}
          {aba === 'analitica' && (
            <div className="card p-0">
              <FiltroTabela
                valor={filtroAnalitica}
                onChange={setFiltroAnalitica}
                placeholder="Filtrar por arquiteto, data, código, valor, pagamento ou status"
              />
              <div className="tabela-container">
                <table className="table table-hover table-arq mb-0">
                  <thead>
                    <tr>
                      {mostrarArquiteto && (
                        <SortTh col="nome" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                          Arquiteto
                        </SortTh>
                      )}
                      <SortTh col="emissao" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                        Emissão
                      </SortTh>
                      <SortTh col="codigo" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                        Código
                      </SortTh>
                      <SortTh col="credito" sort={sortAna} onSort={col => toggleSort(setSortAna, col)} className="text-end">
                        Valor
                      </SortTh>
                      <SortTh col="info" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                        Informação
                      </SortTh>
                      <SortTh col="pagamento" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                        Data Pgto / Baixa
                      </SortTh>
                      <SortTh col="fpagamento" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                        Pagamento
                      </SortTh>
                      <SortTh col="fase" sort={sortAna} onSort={col => toggleSort(setSortAna, col)}>
                        Status
                      </SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {analiticaFiltrada.length === 0 ? (
                      <tr>
                        <td colSpan={colSpanAna} className="text-center text-muted py-4">
                          Nenhum registro encontrado
                        </td>
                      </tr>
                    ) : analiticaFiltrada.map((r, i) => {
                      const np = extrairNumped(r.codigo);
                      return (
                      <tr
                        key={r.conta_oid}
                        className={np ? 'linha-clicavel' : undefined}
                        onClick={np ? () => setNumpedDetalhe(np) : undefined}
                        title={np ? 'Clique para ver o detalhe do pedido' : undefined}
                      >
                        {mostrarArquiteto && <td className="fw-semibold">{r.nome}</td>}
                        <td className="text-muted">{dataFmt(r.emissao)}</td>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#555' }}>
                            {r.codigo}
                          </span>
                        </td>
                        <td className="text-end fw-semibold">{brl(r.credito)}</td>
                        <td className="text-muted" style={{ fontSize: '0.88rem', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.info || '—'}
                        </td>
                        <td className="text-muted" style={{ fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                          {dataFmt(r.pagamento)}
                        </td>
                        <td className="text-muted" style={{ fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                          {r.fpagamento
                            ? <>{r.fpagamento}{r.caixa ? <span className="text-muted"> · {r.caixa}</span> : null}</>
                            : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <FaseBadge fase={r.fase} diasEmissao={r.dias_emissao} pagamento={r.pagamento} info={r.info} codigo={r.codigo} />
                          <CodigoBadge codigo={r.codigo} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {detalhe && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold mb-1">Comissões de {detalhe.titulo}</h5>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {registrosDetalhe.length} registro(s) · {brl(totalDetalhe)}
                    </div>
                  </div>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={fecharDetalhe}></button>
                </div>
                <div className="modal-body p-0">
                  <FiltroTabela
                    valor={filtroDetalhe}
                    onChange={setFiltroDetalhe}
                    placeholder="Filtrar detalhe por data, código, valor, pagamento ou status"
                  />
                  <div className="tabela-container">
                    <table className="table table-hover table-arq mb-0">
                      <thead>
                        <tr>
                          {detalhe.mostrarArquiteto && (
                            <SortTh col="nome" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                              Arquiteto
                            </SortTh>
                          )}
                          <SortTh col="emissao" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                            Emissão
                          </SortTh>
                          <SortTh col="codigo" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                            Código
                          </SortTh>
                          <SortTh col="credito" sort={sortDet} onSort={col => toggleSort(setSortDet, col)} className="text-end">
                            Valor
                          </SortTh>
                          <SortTh col="info" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                            Informação
                          </SortTh>
                          <SortTh col="pagamento" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                            Data Pgto / Baixa
                          </SortTh>
                          <SortTh col="fpagamento" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                            Pagamento
                          </SortTh>
                          <SortTh col="fase" sort={sortDet} onSort={col => toggleSort(setSortDet, col)}>
                            Status
                          </SortTh>
                        </tr>
                      </thead>
                      <tbody>
                        {registrosDetalhe.length === 0 ? (
                          <tr>
                            <td colSpan={detalhe.mostrarArquiteto ? 8 : 7} className="text-center text-muted py-4">
                              Nenhum registro encontrado
                            </td>
                          </tr>
                        ) : registrosDetalhe.map((r, i) => {
                          const np = extrairNumped(r.codigo);
                          return (
                          <tr
                            key={r.conta_oid}
                            className={np ? 'linha-clicavel' : undefined}
                            onClick={np ? () => setNumpedDetalhe(np) : undefined}
                            title={np ? 'Clique para ver o detalhe do pedido' : undefined}
                          >
                            {detalhe.mostrarArquiteto && <td className="fw-semibold">{r.nome}</td>}
                            <td className="text-muted">{dataFmt(r.emissao)}</td>
                            <td>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#555' }}>
                                {r.codigo}
                              </span>
                            </td>
                            <td className="text-end fw-semibold">{brl(r.credito)}</td>
                            <td className="text-muted" style={{ fontSize: '0.88rem', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.info || '—'}
                            </td>
                            <td className="text-muted" style={{ fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                              {dataFmt(r.pagamento)}
                            </td>
                            <td className="text-muted" style={{ fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                              {r.fpagamento
                                ? <>{r.fpagamento}{r.caixa ? <span className="text-muted"> · {r.caixa}</span> : null}</>
                                : '—'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <FaseBadge fase={r.fase} diasEmissao={r.dias_emissao} pagamento={r.pagamento} info={r.info} codigo={r.codigo} />
                              <CodigoBadge codigo={r.codigo} />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={fecharDetalhe}></div>
        </>
      )}

      {/* Modal de detalhe do pedido (click na linha da analítica) */}
      <ModalDetalhePedido
        numped={numpedDetalhe}
        aberto={!!numpedDetalhe}
        aoFechar={() => setNumpedDetalhe(null)}
      />
    </div>
  );
}
